import type { AppBootstrap, ClientAccessSettings, TeamUpdateRequest } from "@repo/shared/types";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getClientAccessImpact, updateClientAccessSettings } from "@/lib/api/client-access";
import * as api from "@/lib/api/teams";
import { reassignClients } from "@/lib/api/users";
import { dashboardKeys } from "@/lib/hooks/use-dashboard";
import { shouldRetryQuery } from "@/providers/query-provider";

const CLIENT_ACCESS_IMPACT_KEY = ["clientAccess", "impact"] as const;

export function useTeams(options?: { enabled?: boolean }) {
  const { data, error, isLoading } = useQuery({
    queryKey: ["teams"],
    queryFn: api.listTeams,
    enabled: options?.enabled ?? true,
  });
  return { teams: data ?? [], error, isLoading };
}

export function useMyTeamIds(options?: { enabled?: boolean }) {
  const { data } = useQuery({
    queryKey: ["teams", "mine"],
    queryFn: api.listMyTeamIds,
    enabled: options?.enabled ?? true,
  });
  return data?.ids ?? [];
}

/**
 * QL-380 — everything a change to client access data can change: a team edit,
 * a client create / edit / delete (its teams, owner, privacy) or an owner
 * reassignment. It changes which clients are visible (lists, dashboard), the
 * teams' client counts, and the Admin impact counts.
 */
export function useInvalidateClientAccessData() {
  const qc = useQueryClient();
  return () =>
    Promise.all([
      qc.invalidateQueries({ queryKey: ["teams"] }),
      qc.invalidateQueries({ queryKey: ["clients"] }),
      qc.invalidateQueries({ queryKey: dashboardKeys.all }),
      qc.invalidateQueries({ queryKey: CLIENT_ACCESS_IMPACT_KEY }),
    ]);
}

/** Admin: move every client of one user to another (Settings → Users). */
export function useReassignClients() {
  const qc = useQueryClient();
  const invalidate = useInvalidateClientAccessData();
  return useMutation({
    mutationFn: ({ fromUserId, toUserId }: { fromUserId: string; toUserId: string }) =>
      reassignClients(fromUserId, { toUserId }),
    onSuccess: () =>
      Promise.all([invalidate(), qc.invalidateQueries({ queryKey: ["users"] })]).then(() => {}),
  });
}

/**
 * Admin only (the endpoint answers 403 otherwise). Client edits anywhere in the
 * app change these counts, so it is always refetched when the settings card
 * mounts rather than served from the 60 s cache.
 */
export function useClientAccessImpact(
  target: ClientAccessSettings,
  options?: { enabled?: boolean },
) {
  const { data, error, isLoading, isFetching, isPlaceholderData, refetch } = useQuery({
    queryKey: [...CLIENT_ACCESS_IMPACT_KEY, target.privateEnabled, target.teamAccessEnabled],
    // Keeps the team count steady while a new target loads. The buckets then belong to the
    // previous target: callers must not word them while `isPlaceholderData` is true.
    placeholderData: keepPreviousData,
    queryFn: () => getClientAccessImpact(target),
    enabled: options?.enabled ?? true,
    staleTime: 0,
    // One retry, so a failure reaches the card's Retry button quickly; 4xx never retries.
    retry: (failureCount, error) => failureCount < 1 && shouldRetryQuery(failureCount, error),
  });
  return { impact: data ?? null, error, isLoading, isFetching, isPlaceholderData, refetch };
}

export function useCreateTeam() {
  const invalidate = useInvalidateClientAccessData();
  return useMutation({ mutationFn: api.createTeam, onSuccess: invalidate });
}

export function useUpdateTeam() {
  const invalidate = useInvalidateClientAccessData();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: TeamUpdateRequest }) => api.updateTeam(id, body),
    onSuccess: invalidate,
  });
}

export function useDeleteTeam() {
  const invalidate = useInvalidateClientAccessData();
  return useMutation({ mutationFn: api.deleteTeam, onSuccess: invalidate });
}

export function useUpdateClientAccessSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateClientAccessSettings,
    // The settings decide every visibility-filtered read: lists, views, dashboard.
    // Awaited, so the caller's onSuccess runs once the bootstrap already carries
    // the new settings (no flash of the old ones).
    onSuccess: async (settings) => {
      qc.setQueryData<AppBootstrap | null>(["app-bootstrap"], (prev) =>
        prev ? { ...prev, clientAccess: settings } : prev,
      );
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["app-bootstrap"] }),
        qc.invalidateQueries({ queryKey: ["clients"] }),
        qc.invalidateQueries({ queryKey: ["clientViews"] }),
        qc.invalidateQueries({ queryKey: dashboardKeys.all }),
        qc.invalidateQueries({ queryKey: CLIENT_ACCESS_IMPACT_KEY }),
      ]);
    },
  });
}
